import React, { ReactNode } from 'react';
import { rem, transparentize } from 'polished';
import { styled, useTheme } from 'styles';
import { BodyText, Button } from '@unmind/design-system-components-web';
import { isMSTeams } from 'utils/MSTeams';
import { IconProps, Phone } from 'icons';
import AccountMenu from 'Navigation/AccountMenu';
import { useAuth0 } from '@auth0/auth0-react';
import useLogoutWithUniversalLogin from 'App/Auth/useLogoutWithUniversalLogin';
import { useUser } from 'Account/hooks';
import { RouteComponentProps, useLocation, withRouter } from 'react-router';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { withLogout, WithLogoutProps } from 'App/Auth';
import { tracking } from 'App/Tracking';
import RoutePath from 'App/RoutePath';
import { useOrganisationEntitlements } from 'Shared/Entitlement';
import { MainMenuFeatures } from '../MainMenu';
import MenuLink from '../MenuLink';

export const VERTICAL_MENU_WIDTH = 255;
export const VERTICAL_MENU_SIDE_PADDING = 16;

const Wrapper = styled.nav`
  display: flex;
  flex-direction: column;
  width: ${rem(VERTICAL_MENU_WIDTH)};
  justify-content: flex-start;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background.card};
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.header - 1};
`;

const LinkTitle = styled(BodyText).attrs(({ theme }) => ({
  sizes: [theme.typography.fontSizes.fontSize14],
  weight: theme.typography.fontWeights.medium,
  color: theme.colors.text.primary,
}))`
  position: relative;
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  margin-left: ${rem(10)};
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding: ${rem(0)} 0 ${rem(16)};

  &.active span {
    color: ${({ theme }) => theme.colors.text.link};
  }
`;

const MenuItem = styled.div<{ displayedInMSTeams: boolean }>`
  display: flex;
  justify-content: flex-start;
  margin-right: ${({ displayedInMSTeams }) =>
    displayedInMSTeams ? rem(10) : rem(0)};
  width: 100%;
  padding: ${rem(4)} 0;

  &.active span {
    color: ${({ theme }) => theme.colors.text.link};
  }
`;

const NavMenuContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; /* Added for MS Teams */
  display: flex;
  flex-direction: column;
  padding: ${rem(24)} ${rem(VERTICAL_MENU_SIDE_PADDING)};
`;

const BottomMenuContainer = styled.div`
  margin-top: auto;
  padding: ${rem(16)};
`;

const GetHelpNowButton = styled(Button)`
  width: 100%;
  background-color: ${({ theme }) =>
    `${transparentize(0.6, theme.colors.background.error)}`};
  margin-bottom: ${rem(16)};
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.error};
  }

  &:focus {
    outline: none;
  }
`;

const GetHelpNowButtonTextContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GetHelpNowButtonText = styled(BodyText).attrs(({ theme }) => ({
  sizes: [theme.typography.fontSizes.fontSize14],
  weight: theme.typography.fontWeights.semiBold,
  color: theme.colors.text.primary,
}))`
  line-height: 100%;
  text-align: center;
`;

const GetHelpNowButtonIcon = styled(Phone).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  width: 18,
  height: 18,
}))`
  margin-right: ${rem(4)};
`;

export interface LinkConfig {
  name: string;
  to: string;
  Icon: React.FC<IconProps>;
  shouldHide?: boolean;
  feature?: MainMenuFeatures;
  label?: string;
}

export interface VerticalMenuProps
  extends RouteComponentProps,
    WithLogoutProps {
  links: LinkConfig[];
  logoComponent?: ReactNode;
  activeFeature?: MainMenuFeatures;
  onFeatureClick(feature?: MainMenuFeatures): void;
}

const VerticalMenu = ({
  links,
  logoComponent,
  activeFeature,
  onFeatureClick,
  logout,
  history,
}: VerticalMenuProps) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth0();
  const { logoutWithUniversalLogin } = useLogoutWithUniversalLogin();
  const { permissions, userType } = useUser();
  const { entitlements } = useOrganisationEntitlements([
    { entitlement: 'unmindHelp' },
  ]);
  const { t: translate } = useTranslation('navigation');
  const location = useLocation();

  const onNavigate = (path: string) => {
    history.push(path);
  };

  return (
    <Wrapper>
      <NavMenuContainer>
        {logoComponent && <LogoContainer>{logoComponent}</LogoContainer>}
        {links
          .filter(({ shouldHide }) => !shouldHide)
          .map(({ name, to, Icon, feature }) => (
            <MenuItem key={name} displayedInMSTeams={isMSTeams()}>
              <MenuLink
                to={to}
                data-testid={`${feature}-menu-link`}
                $isInFooter={false}
                onClick={() => onFeatureClick(feature)}
                $isSelected={feature === activeFeature}
              >
                <Icon
                  data-testid={`${feature}-icon`}
                  primaryColor={theme.colors.text.primary}
                  secondaryColor={theme.colors.text.primary}
                  width={18}
                  height={18}
                />
                <LinkTitle>{name}</LinkTitle>
              </MenuLink>
            </MenuItem>
          ))}
      </NavMenuContainer>
      <BottomMenuContainer>
        <GetHelpNowButton
          to={
            entitlements.unmindHelp
              ? `${RoutePath.Help}#immediate-support`
              : RoutePath.Help
          }
          onClick={() => {
            tracking.track('help_immediate_support_clicked', {
              origin: location.pathname,
              unmindHelpEnabled: entitlements.unmindHelp,
              uiLocation: 'vertical-menu',
            });
          }}
          label={
            <GetHelpNowButtonTextContainer>
              <GetHelpNowButtonIcon />
              <GetHelpNowButtonText>
                {translate('main_menu.get_help_now')}
              </GetHelpNowButtonText>
            </GetHelpNowButtonTextContainer>
          }
          ariaLabel={translate('main_menu.get_help_now')}
        />
        <AccountMenu
          permissions={permissions}
          userType={userType}
          onLogout={async () => {
            if (isAuthenticated) {
              await logoutWithUniversalLogin();
            } else {
              await logout();

              if (isMSTeams()) {
                history.replace(RoutePath.StartSplitScreen);
              } else {
                history.replace(RoutePath.Login);
              }
            }
          }}
          onNavigate={onNavigate}
          isInVerticalMenu={true}
        />
      </BottomMenuContainer>
    </Wrapper>
  );
};

export default compose<VerticalMenuProps, Record<string, unknown>>(
  withLogout,
  withRouter,
)(VerticalMenu);
